import type { Card, CbEvent, CbTableState, Envelope } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { signal } from "../net/signal";
import { showCarpet } from "../game/carpet";
import { Sequencer } from "../scene/queue";
import { introGate } from "../game/stake";
import { applyCb, emptyCb, forbidden, isPrize, sameCard, toLocal, toLocalUnit, toServer, type CbLocal } from "./state";
import type { CbScene } from "./scene";

/**
 * کنترلرِ میزِ آنلاینِ چهاربرگ — پورتِ CharBargOnlineController.
 *
 * تفاوتِ اصلی با حکم: وقتی یک کارت چند ترکیبِ برداشت دارد، سرور کارت را اعمال
 * نمی‌کند و منتظرِ انتخابِ ماست — پس انتخابگرِ ترکیب با وضعیتِ سرور باز و بسته
 * می‌شود، نه با تصمیمِ محلی.
 */
export type CbOverlay =
  | { kind: "round"; unit: number; roundScore: number[] }
  | { kind: "match"; unit: number };

export class CbOnline {
  private seq = new Sequencer();
  private scene: CbScene;
  private off: (() => void) | null = null;
  private gotState = false; private seatsLaid = false; private quitting = false; private carpetApplied = false;
  private st: CbLocal = emptyCb();
  private awaitingHumanPlay = false; private awaitingCapture = false;
  /** دستِ بعدی، شروعِ دور است؟ فقط آن یکی کارت‌های زمین را هم پخش می‌کند. */
  private roundStart = true;
  private startShown = false;
  private ceremonyDone = false;
  private turnEndsAt = 0; private turnTotal = 0; private tick: ReturnType<typeof setInterval> | null = null;

  readonly overlay = signal<CbOverlay | null>(null);
  /** انتخابگرِ ترکیب — ری‌اکت می‌کشد؛ null یعنی بسته. */
  readonly picker = signal<{ played: Card; options: Card[][]; best: number } | null>(null);
  readonly raw = signal<CbTableState | null>(null);

  constructor(scene: CbScene) { this.scene = scene; }
  get state(): CbLocal { return this.st; }

  start(): void {
    this.off = client.on((env) => this.onMessage(env));
    // پرده‌ی شرط بالاست؟ صحنه پیام‌ها را از همین حالا می‌گیرد، ولی تا
    // بالا رفتنِ پرده چیزی نمی‌کشد — وگرنه آس‌کشی و پخشِ ورق پشتِ پرده
    // انجام می‌شد و بازیکن میزی را می‌دید که ورق‌ها در دستش سبز شده‌اند.
    this.seq.hold(introGate());
    // پخشِ دستِ تازه پیش از سوارشدنِ این صفحه رسیده؛ از سیگنال برش می‌داریم.
    const ace = client.cbAce.value;
    if (ace) this.seq.enqueue(() => this.playEvent(ace));
    const deal = client.cbDeal.value;
    if (deal) this.seq.enqueue(() => this.playEvent(deal));
    const last = client.cbTable.value;
    if (last) this.seq.enqueueSnapshot(() => this.applyState(last));
    this.tick = setInterval(() => this.updateTimer(), 200);
    (window as unknown as { __cb?: CbOnline }).__cb = this;
  }

  dispose(): void {
    this.quitting = true;
    this.off?.(); this.off = null;
    this.seq.dispose();
    if (this.tick) clearInterval(this.tick);
  }

  // ---------------------------------------------------------------- ورودیِ بازیکن

  clickCard(i: number): void {
    if (this.quitting || !this.awaitingHumanPlay) return;
    const card = this.st.myHand[i];
    if (!card) return;
    if (forbidden(this.st, card)) { this.scene.toast("روی زمینِ خالی سرباز نمی‌اندازند — کارتِ دیگری بازی کن"); return; }
    this.awaitingHumanPlay = false;
    this.scene.setAwaitingHuman(false);
    this.turnEndsAt = 0; this.scene.setTimer(0, 0);
    client.cbPlay(card);
  }

  pickCapture(index: number): void {
    if (this.quitting || !this.awaitingCapture) return;
    if (index < 0 || index >= this.st.captureOptions.length) return;
    this.awaitingCapture = false;
    this.picker.value = null;
    this.turnEndsAt = 0; this.scene.setTimer(0, 0);
    client.cbPickCapture(index);
  }

  /** دورِ بعد را سرور شروع می‌کند؛ دکمه فقط پنل را می‌بندد. */
  nextRound(): void { this.overlay.value = null; }
  seatName(l: number): string { return l === 0 ? "تو" : (this.st.names[l] || "حریف"); }

  // ---------------------------------------------------------------- پیام‌های سرور

  private onMessage(env: Envelope): void {
    if (this.quitting) return;
    switch (env.msg.case) {
      case "cbState": { const ts = env.msg.value; this.seq.enqueueSnapshot(() => this.applyState(ts)); break; }
      case "cbEvent": { const ev = env.msg.value; this.seq.enqueue(() => this.playEvent(ev)); break; }
      case "error": this.showError(env.msg.value.code); break;
    }
  }

  private showError(code: string): void {
    const msg: Record<string, string> = { not_your_turn: "نوبتِ تو نیست", not_in_hand: "این کارت در دستت نیست", jack_on_empty: "روی زمینِ خالی سرباز نمی‌اندازند" };
    if (!msg[code]) return;
    this.scene.toast(msg[code]!);
    this.awaitingHumanPlay = !this.st.roundOver && this.st.turn === 0 && !this.awaitingCapture;
    this.scene.setAwaitingHuman(this.awaitingHumanPlay);
  }

  private async applyState(ts: CbTableState): Promise<void> {
    if (!this.carpetApplied) { this.carpetApplied = true; showCarpet(ts.carpet); }
    this.st = applyCb(ts);
    this.raw.value = ts;
    this.gotState = true;

    // پرچم‌های نوبت **پیش از** رندر — نما موقعِ کشیدنِ دست از همین‌ها می‌پرسد.
    this.awaitingHumanPlay = this.st.myTurn;
    this.awaitingCapture = this.st.awaitingCapture;
    this.scene.setAwaitingHuman(this.awaitingHumanPlay);

    if (!this.seatsLaid) { this.scene.layoutSeats(this.st); this.seatsLaid = true; }
    this.scene.render(this.st);
    this.scene.renderTable(this.st);

    if (this.overlay.value && !this.st.roundOver && !this.st.matchOver) this.overlay.value = null;

    if (this.awaitingCapture && !this.picker.value && this.st.capturePlayed) {
      this.picker.value = { played: this.st.capturePlayed, options: this.st.captureOptions, best: this.st.captureBest };
      this.scene.toast("کدام ترکیب را می‌بری؟");
    } else if (!this.awaitingCapture && this.picker.value) this.picker.value = null;

    if ((this.awaitingHumanPlay || this.awaitingCapture) && this.st.turnRemaining > 0.1) {
      this.turnTotal = Math.max(this.turnTotal, this.st.turnRemaining);
      this.turnEndsAt = performance.now() + this.st.turnRemaining * 1000;
    } else { this.turnEndsAt = 0; this.turnTotal = 0; this.scene.setTimer(0, 0); }
  }

  private updateTimer(): void {
    if (this.turnEndsAt <= 0 || !(this.awaitingHumanPlay || this.awaitingCapture)) return;
    const left = (this.turnEndsAt - performance.now()) / 1000;
    if (left <= 0) { this.turnEndsAt = 0; this.scene.setTimer(0, 0); return; }
    this.scene.setTimer(left, Math.max(left, this.turnTotal));
  }

  private async playEvent(ev: CbEvent): Promise<void> {
    // «پخش» خودش وضعیت را از سیگنال برمی‌دارد، چون پیش از اولین وضعیت می‌رسد؛
    // بقیه‌ی رویدادها بی‌وضعیت معنا ندارند. بی این استثنا، همان نگهبانِ یونیتی
    // پخشِ ذخیره‌شده را پیش از رسیدن به case می‌کُشت: کارت‌ها یک‌باره روی صفحه
    // بودند و roundStart هم روی true گیر می‌کرد، پس دسته‌ی بعدی بی‌خود کارتِ
    // زمین می‌ریخت.
    if (!this.gotState && ev.kind !== "deal" && ev.kind !== "ace_draw") return;
    const n = this.st.n;
    const local = toLocal(ev.seat, this.st.me, n);
    switch (ev.kind) {
      case "played": {
        if (!ev.card) return;
        const card = ev.card;
        // تماشاچی دستی ندارد — [[HokmOnline.playEvent]]
        if (local === 0 && !this.st.watching) {
          const at = this.st.myHand.findIndex((c) => sameCard(c, card));
          if (at >= 0) this.st.myHand.splice(at, 1);
          this.st.counts[0] = this.st.myHand.length;
        } else if (local >= 0 && local < n && (this.st.counts[local] ?? 0) > 0) this.st.counts[local]!--;
        this.scene.render(this.st);
        await this.scene.animPlay(local, card);
        if (ev.captured.length === 0) {
          // روی زمین ماند — اگر امتیازدار است، لحظه‌ی خودش را دارد
          this.st.table.push(card);
          this.scene.renderTable(this.st);
          if (isPrize(card)) await this.scene.animPrizeOnTable(this.st.table.length - 1, card);
        } else {
          for (const c of ev.captured) {
            const at = this.st.table.findIndex((t) => sameCard(t, c));
            if (at >= 0) this.st.table.splice(at, 1);
          }
          this.scene.renderTable(this.st);
          await this.scene.animCapture(local, card, [...ev.captured]);
        }
        break;
      }
      case "sur":
        this.scene.toast(local === 0 && !this.st.watching ? "سور! زمین را جارو کردی" : `${this.seatName(local)} سور کرد`);
        break;
      case "sur_cancelled": {
        const victim = toLocal(ev.victim, this.st.me, n);
        if (ev.card) await this.scene.animSurCancel(local, ev.card, [], victim, ev.victimCard ?? null);
        break;
      }
      case "ace_draw": {
        // مراسم پیش از هر چیز: صحنه باید صندلی‌ها را داشته باشد، پس اگر
        // وضعیت هنوز نرسیده با آخرین نمای سرور می‌سازیمش — [[HokmOnline]].
        if (this.ceremonyDone || ev.draw.length === 0) return;
        this.ceremonyDone = true;
        const fresh0 = client.cbTable.value;
        if (fresh0) { this.st = applyCb(fresh0); this.raw.value = fresh0; this.gotState = true; }
        if (!this.seatsLaid) { this.scene.layoutSeats(this.st); this.seatsLaid = true; }
        // میزِ نپخش: وضعیتی که سرور فرستاده دستِ پخش‌شده را هم دارد، ولی مراسم
        // **پیش از** پخش است. اگر خودِ st را بدهیم، بازیکن دستش را می‌بیند و
        // بعد آس کشیده می‌شود — یعنی وارونه. پخش کارِ رویدادِ deal است.
        this.scene.render(beforeDeal(this.st));
        const from = toLocal(ev.fromSeat, this.st.me, this.st.n);
        await this.scene.animStarterDraw(ev.draw, from, local, this.seatName(local));
        await this.scene.animShuffle();
        this.scene.render(beforeDeal(this.st));
        client.cbAce.value = null;
        break;
      }

      case "deal": {
        // وضعیتِ تازه را همین‌جا برمی‌داریم: رویداد پیش از پخشِ وضعیت می‌آید و
        // دستِ کهنه هنوز روی this.st است. applyState بلافاصله بعد همین را می‌نشانَد.
        const fresh = client.cbTable.value;
        if (fresh) { this.st = applyCb(fresh); this.raw.value = fresh; this.gotState = true; }
        if (!this.gotState) break;
        if (!this.carpetApplied) { this.carpetApplied = true; showCarpet(this.st.carpet); }
        if (!this.seatsLaid) { this.scene.layoutSeats(this.st); this.seatsLaid = true; }
        // کارت‌های زمین فقط سرِ شروعِ دور پخش می‌شوند؛ دسته‌های میانی فقط دست.
        const toTable = this.roundStart ? this.st.table.length : 0;
        this.roundStart = false;
        await this.scene.animDeal(this.st, Math.min(8, Math.max(1, this.st.watching ? (this.st.counts[0] ?? 4) : this.st.myHand.length)),
          toTable, toLocal(ev.seat, this.st.me, this.st.n));
        // فقط یک بار، سرِ شروعِ مسابقه — نه سرِ هر دسته‌ی میانی.
        if (!this.startShown) { this.startShown = true; await this.scene.animStartBanner("شروع!"); }
        client.cbDeal.value = null;
        break;
      }
      case "round_over": {
        const units = Math.max(1, this.st.units);
        const unit = toLocalUnit(ev.winnerUnit, this.st.me, units);
        this.st.roundOver = true;
        const rs = new Array(units).fill(0).map((_, u) => ev.roundScore[toServer(u, this.st.me, units)] ?? 0);
        this.overlay.value = { kind: "round", unit, roundScore: rs };
        this.roundStart = true;   // دورِ بعد دوباره کارت‌های زمین را هم پخش می‌کند
        break;
      }
      case "match_over": {
        const unit = toLocalUnit(ev.winnerUnit, this.st.me, Math.max(1, this.st.units));
        this.st.matchOver = true;
        this.overlay.value = { kind: "match", unit };
        break;
      }
      case "seat_bot":
        this.scene.toast(`${this.seatName(local)} رفت — ربات جایش نشست`);
        break;
      case "seat_reconnect":
        this.scene.toast(`${this.seatName(local)} برگشت`);
        break;
    }
  }
}

/**
 * نمای میز پیش از پخشِ کارت — دست‌ها خالی، زمین خالی، دسته‌های جمع‌شده صفر،
 * نوبت هیچ‌کس.
 *
 * مراسمِ آس‌کشی پیش از پخش است، ولی وضعیتی که سرور همان لحظه فرستاده کارت‌ها را
 * هم دارد. این‌جا فقط نما را عقب می‌بریم؛ خودِ st دست نمی‌خورد تا رویدادِ deal
 * بتواند همان را پخش کند.
 */
function beforeDeal(st: CbLocal): CbLocal {
  return { ...st, myHand: [], counts: st.counts.map(() => 0), piles: st.piles.map(() => 0), surCards: st.surCards.map(() => []), table: [], turn: -1 };
}
